import { ChangeDetectionStrategy, Component, computed, input, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DpIconComponent } from '@client-monorepo/common/icon';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DefaultTransitionFunctionConst } from '../../data-access/constants/default-transition-function.const';
import { NgxSkeletonLoadingComponent } from '@digipay/ngx-skeleton-loading';

@Component({
  selector: 'common-ui-components-chip',
  standalone: true,
  imports: [CommonModule, DpIconComponent, NgxSkeletonLoadingComponent],
  templateUrl: './chip.component.html',
  styleUrl: './chip.component.scss',
  animations: [
    trigger('appear', [
      state(
        'void',
        style({
          width: 0,
        }),
      ),
      state(
        '*',
        style({
          width: '*',
        }),
      ),
      transition('void <=> *', [animate('0.35s ' + DefaultTransitionFunctionConst)]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipComponent {
  // Inputs
  id = input.required<string>();
  size = input<'SMALL' | 'MEDIUM' | 'LARGE'>('MEDIUM');
  disabled = input<boolean>(false);
  disableAnimations = input<boolean>(false);
  isLoading = input<boolean>(false);
  leadingIcon = input<string>('');
  disableClick = input<boolean>(false);
  hideLabel = input<boolean>(false);
  trailingIcon = input<string>('');
  label = input<string>('');
  stopPropagationOnTrailingIconClick = input<boolean>(true);
  classes = input<string>('');
  isLeadingIconTinted = input<boolean>(false);
  leadingIconType = input<'bold' | 'linear' | 'due'>('linear');
  leadingIconColor = input<string>('');
  leadingIconStyle = input<string>('');
  trailingIconType = input<'bold' | 'linear' | 'due'>('linear');
  isTrailingIconTinted = input<boolean>(false);
  trailingIconColor = input<string>('');
  trailingIconStyle = input<string>('');

  // Outputs
  trailingIconClicked = output<string>();

  // Models
  pressed = model<boolean>(false);

  // Variables
  hovered = signal<boolean>(false);
  generatedTextClasses = computed(() => (this.disabled() ? 'text-onback-disabled' : this.pressed() ? 'text-onback-brand' : ''));
  generatedWrapperClasses = computed(() => this.generateWrapperClasses());
  sizeToTextTypographyMap = {
    SMALL: 'l-2',
    MEDIUM: 'c-2',
    LARGE: 'bt-2',
  };

  handleTrailingChipClick(event: any): void {
    if (!this.disabled()) {
      if (this.stopPropagationOnTrailingIconClick()) {
        event.stopPropagation();
        event.preventDefault();
      }
      this.trailingIconClicked.emit(this.id());
    }
  }

  handleWrapperHover(isHovered: boolean): void {
    this.hovered.set(isHovered);
  }

  handlePress(): void {
    if (this.disableClick()) {
      return;
    }
    if (!this.disabled()) {
      this.pressed.set(!this.pressed());
    }
  }

  generateWrapperClasses(): string {
    const size = this.size();
    const classes = [];

    // Size-based classes
    if (size === 'LARGE') {
      classes.push('p-small', 'h-40');
    } else if (size === 'MEDIUM') {
      classes.push('px-small', 'py-tiny', 'h-28');
    } else if (size === 'SMALL') {
      classes.push('px-low', 'py-tiny', 'h-24');
    }
    // Hover-based classes
    classes.push(this.hovered() ? 'border-500' : 'border-300');

    // Pressed and disabled-based classes
    if (this.pressed()) {
      classes.push(this.disabled() ? 'surface-back' : 'border-color-brand surface-brand-tint');
    } else if (this.disabled()) {
      classes.push('border-color-disabled');
    }

    return classes.join(' ');
  }
}
